<script setup lang="ts">
import type { PageTileFragment } from '#graphql-operations'
import { vOnClickOutside } from '@vueuse/components'
import { ref, nextTick, onMounted } from 'vue'
import { useRoute } from 'vue-router'

defineProps<{ pages: PageTileFragment[] }>()
const { loadIcon } = useDynamicIcons()
const isOpen = ref(false)
const buttonRef = ref<HTMLElement | null>(null)
const listRef = ref<HTMLElement | null>(null)
const activeIndex = ref(-1)
const route = useRoute()

function openMenu() {
  isOpen.value = true
  nextTick(() => {
    listRef.value?.focus()
  })
}

function closeMenu() {
  if (isOpen.value) {
    isOpen.value = false
    activeIndex.value = -1
    nextTick(() => {
      buttonRef.value?.focus()
    })
  }
}

function onButtonKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    openMenu()
    activeIndex.value = 0
  }
}

function onListKeydown(e: KeyboardEvent, pages: PageTileFragment[]) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % pages.length
  }
  else if (e.key === 'ArrowUp') {
    e.preventDefault()
    activeIndex.value = (activeIndex.value - 1 + pages.length) % pages.length
  }
  else if (e.key === 'Home') {
    e.preventDefault()
    activeIndex.value = 0
  }
  else if (e.key === 'End') {
    e.preventDefault()
    activeIndex.value = pages.length - 1
  }
  else if (e.key === 'Tab') {
    closeMenu()
  }
  else if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    if (activeIndex.value >= 0 && activeIndex.value < pages.length) {
      const url = pages[activeIndex.value]?.PageUrl?.url
      if (url) {
        navigateTo(`/${url}/`)
      }
    }
  }
}

function keypress(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('keydown', keypress)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keypress)
  closeMenu()
})
</script>

<template>
  <div
    v-on-click-outside="closeMenu"
    class="cms-dropdown-menu"
    :class="{ 'cms-dropdown-menu--open': isOpen }"
  >
    <button
      ref="buttonRef"
      class="cms-dropdown-menu__button"
      type="button"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      aria-controls="dropdown-listbox"
      @click="isOpen = !isOpen"
      @keydown="onButtonKeydown"
    >
      <span>Kies een categorie</span>
      <NuxtIcon name="icon:chevron" />
    </button>

    <div class="cms-dropdown-menu__panel">
      <ul
        id="dropdown-listbox"
        ref="listRef"
        class="cms-dropdown-menu__list"
        role="listbox"
        tabindex="-1"
        :aria-activedescendant="activeIndex >= 0 ? 'dropdown-option-' + pages[activeIndex]?.documentId : undefined"
        @keydown="(e) => onListKeydown(e, pages)"
      >
        <li
          v-for="(page, idx) in pages"
          :id="'dropdown-option-' + page.documentId"
          :key="page.documentId"
          class="cms-dropdown-menu__item"
          :class="{
            'cms-dropdown-menu__item--active :focus-visible': idx === activeIndex,
            'cms-dropdown-menu__item--selected': page.PageUrl?.url === route.path.slice(1, -1),
          }"
          role="option"
          :aria-selected="idx === activeIndex"
        >
          <AppLink
            v-if="page.PageUrl?.url"
            :to="`/${page.PageUrl.url}/`"
            class="cms-dropdown-menu__link"
            tabindex="-1"
            @click="closeMenu()"
          >
            <NuxtIcon :name="loadIcon(page.Icon)" />
            <span>{{ page.PageTitle }}</span>
          </AppLink>
        </li>
      </ul>
    </div>

    <!-- <pre>$route.path: {{ $route.path.slice(1, -1) }}</pre> -->
    <!-- <pre>pages: {{ pages }}</pre> -->
  </div>
</template>

<style>
@import url("~layers/components/Cms/DropwdownMenu.css");
</style>
